import React, { Component, useContext } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Notification } from '@progress/kendo-react-notification';
import languages from '../lang/languages.json';
import ViewAlertEvents from '../alerts/AlertEvents';
import logo from '../logo/logo_text_03.png';
import sideLogo from '../logo/side_menu_logo.png';
import agscoutLogo from '../images/agscout_small.png'
import { NavLink, Outlet } from 'react-router-dom';
import UserSelector from './UserSelector';
import FloatingDropdown from './FloatingDropdown';
import AppContext from '../context/AppContext';
import { retrieveToken, getAuthToken, handleAuthTokenError, BASEURI } from '../utils';

export class RefreshPortal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    const refreshContainer = document.getElementById("refresh-container");
    refreshContainer.appendChild(this.el);
  }

  componentWillUnmount() {
    const refreshContainer = document.getElementById("refresh-container");
    refreshContainer.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(
      this.props.children,
      this.el
    );
  }
}

function SharedTabDropdown({ sharedTab, userId, reloadSharedTabs, showToast }) {
  const intl = useIntl();

  const handleRemove = () => {
    showToast(intl.formatMessage({ id: 'dashboardSharing.toast.removingDashboard', defaultMessage: 'Removing dashboard...' }), 'info');
    getAuthToken()
      .then(token => token)
      .catch(handleAuthTokenError)
      .then(token => {
        if (token === false) {
          console.error("Authentication token not found.");
          return;
        }
        fetch(`${BASEURI}/api/dashboard-charts/tabs/${sharedTab.id}/remove-shared-user/${userId}/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        .then(response => {
          if (!response.ok) {
            return response.json().then(data => { throw new Error(data.error || 'Failed to remove user'); });
          }
          showToast(intl.formatMessage({ id: 'dashboardSharing.toast.dashboardRemoved', defaultMessage: 'Dashboard removed.' }), 'success');
          if (reloadSharedTabs) reloadSharedTabs();
        })
        .catch(error => {
          console.error("Error removing accepted user:", error);
        });
      });
  };

  return (
    <FloatingDropdown
      options={[
        {
          label: (
            <span className="flex w-full min-w-0 flex-row items-center justify-start gap-2 text-start">
              <i className="material-icons">remove_circle</i>
              {intl.formatMessage({ id: 'dashboardSharing.dropdown.remove', defaultMessage: 'Remove' })}
            </span>
          ),
          onClick: handleRemove
        },
      ]}
    />
  );
}

class Layout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      title: 'AgViewer',
      showFilter: false,
      toasts: []
    };
    this.showToast = this.showToast.bind(this);
    this.handleDuplicateDashboard = this.handleDuplicateDashboard.bind(this);
  }

  handleDuplicateDashboard(tab) {
    return ({ navigate }) => {
      this.showToast(
        this.props.intl.formatMessage({
          id: 'dashboardSharing.dropdown.duplicating',
          defaultMessage: 'Creating a copy...'
        }),
        'info'
      );
      getAuthToken()
        .then(token => token)
        .catch(handleAuthTokenError)
        .then(token => {
          if (token === false) {
            this.showToast(
              this.props.intl.formatMessage({
                id: 'dashboardSharing.dropdown.duplicateAuthError',
                defaultMessage: 'You must be signed in to copy a dashboard.'
              }),
              'error'
            );
            return;
          }
          fetch(`${BASEURI}/api/tools/dashboard/${tab.id}/duplicate/`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: '{}'
          })
            .then(async (response) => {
              let data = {};
              try {
                data = await response.json();
              } catch {
                /* non-JSON body */
              }
              if (!response.ok) {
                const msg =
                  (typeof data.message === 'string' && data.message) ||
                  (typeof data.detail === 'string' && data.detail) ||
                  response.statusText;
                throw new Error(msg || 'Request failed');
              }
              return data;
            })
            .then((data) => {
              if (this.props.loadTabs) {
                this.props.loadTabs();
              }
              this.showToast(
                this.props.intl.formatMessage({
                  id: 'dashboardSharing.dropdown.duplicateSuccess',
                  defaultMessage: 'Dashboard copied.'
                }),
                'success'
              );
              if (data.new_tab_id && navigate) {
                navigate(`/dashboard/${data.new_tab_id}`, {
                  state: { isSharedDash: false }
                });
              }
            })
            .catch((error) => {
              console.error('Duplicate dashboard:', error);
              this.showToast(
                error.message ||
                  this.props.intl.formatMessage({
                    id: 'dashboardSharing.dropdown.duplicateError',
                    defaultMessage: 'Could not copy the dashboard.'
                  }),
                'error'
              );
            });
        });
    };
  }

  showToast(message, type = 'info') {
    this.setState(prev => ({
      toasts: [
        ...prev.toasts,
        { message, type, id: Date.now() + Math.random() }
      ]
    }));
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.toasts.length > 0 && prevState.toasts.length !== this.state.toasts.length) {
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.setState(prev => ({
          toasts: prev.toasts.slice(1)
        }));
      }, 3000);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.toastTimer);
  }

  render() {
    const isRTL = this.props.locale?.localeId === 'ar';
    return (
      <div
        // dir={isRTL ? 'rtl' : 'ltr'}
        className={`mdl-layout mdl-js-layout mdl-layout--fixed-header${isRTL ? ' layout-rtl' : ''}`}
      >
        <header className="mdl-layout__header">
          <div className="mdl-layout__header-row flex justify-between items-center">
            <span className="mdl-layout-title ">
              <img className="h-[40px] sm:h-[40] mt-[-10px] mb-[-10px]" src={logo} alt="AGVIEWER"></img>
            </span>
            <div className='flex justify-between items-center'>
              <div className='hidden sm:!block'>
                <span style={{ marginRight: "20px", fontFamily: "inherit" }}>
                  <FormattedMessage id='app.layout' defaultMessage='Welcome' />, {this.props.username}
                </span>
              </div>
              <ViewAlertEvents />
              <div id="refresh-container"></div>
              <div id='header-locale'>
                <DropDownList
                  style={{ color: 'white', width: '118px', border: 'none' }}
                  textField='locale'
                  dataItemKey="localeId"
                  data={languages}
                  value={this.props.locale}
                  onChange={this.props.handleLocaleChange}
                />
              </div>
              {(this.props.isAdmin || this.props.isOrganization || Boolean(retrieveToken('adminOrOrgToken'))) &&
                <UserSelector
                  users={this.props.users}
                  loadingUsers={this.props.loadingUsers}
                  handleSwitchUser={this.props.handleSwitchUser}
                  usersLength={this.props.usersLength}
                  handleFilterChange={this.props.handleFilterChange}
                  clientId={this.props.clientId}
                  username={this.props.username}
                  country={this.props.country}
                />
              }
            </div>
          </div>
        </header >

        <div className="mdl-layout__drawer" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="ml-auto mr-auto mt-5">
            <img className="w-[100px]" src={sideLogo} alt="AGVIEWER"></img>
          </div>
          <nav className="mdl-navigation">
            <div id='nav-locale'>
              <DropDownList
                style={{ color: 'white', width: '118px', border: 'none' }}
                textField='locale'
                dataItemKey="localeId"
                data={languages}
                value={this.props.locale}
                onChange={this.props.handleLocaleChange}
              />
            </div>
            <span
              className="mdl-navigation__link"
            >
              <i className="material-icons">dashboard</i>
              <span className="nav-link-text">
                <FormattedMessage id='app.dash.nav' defaultMessage='Dashboards' />
              </span>
            </span>
          </nav>
          <div className="ag-dashboards-ctn">
            {this.props.tabList?.sort((a, b) => {
              const numA = parseFloat(a?.name);
              const numB = parseFloat(b?.name);
              const hasNumA = !isNaN(numA);
              const hasNumB = !isNaN(numB);

              if (hasNumA && hasNumB) {
                if (numA !== numB) return numA - numB;
                return a.name?.localeCompare(b?.name);
              }
              if (hasNumA) return -1;
              if (hasNumB) return 1;
              return a.name?.localeCompare(b?.name);
            }).map(tab => {
              return (
                <div key={tab.id} className={"dashboard-item"}>
                  <NavLink
                    to={{ pathname: `dashboard/${tab.id}` }}
                    state={{ isSharedDash: false, tabName: tab.name }}>
                    <span key={tab.id}>
                      {tab.name}
                    </span>
                  </NavLink>
                  {
                    !this.props.isLockedUser &&
                    <div className="inline-block ml-2 rtl:ml-0 rtl:mr-2 align-middle">
                      <FloatingDropdown
                        options={[
                          {
                            label: (
                              <span className="flex w-full min-w-0 flex-row items-center justify-start gap-2 text-start">
                                <i className="material-icons">edit</i>
                                {this.props.intl.formatMessage({ id: 'dashboardSharing.dropdown.edit', defaultMessage: 'Edit' })}
                              </span>
                            ),
                            onClick: (e) => this.props.open(e, 'edit', tab.id)
                          },
                          {
                            label: (
                              <span className="flex w-full min-w-0 flex-row items-center justify-start gap-2 text-start">
                                <i className="material-icons">content_copy</i>
                                {this.props.intl.formatMessage({
                                  id: 'dashboardSharing.dropdown.duplicateCopy',
                                  defaultMessage: 'Make a copy'
                                })}
                              </span>
                            ),
                            onClick: this.handleDuplicateDashboard(tab)
                          },
                          {
                            label: (
                              <span className="flex w-full min-w-0 flex-row items-center justify-start gap-2 text-start">
                                <i className="material-icons">share</i>
                                {this.props.intl.formatMessage({ id: 'dashboardSharing.dropdown.share', defaultMessage: 'Share' })}
                              </span>
                            ),
                            onClick: ({navigate}) => {
                                navigate('/dashboard-sharing', { state: { tabId: tab.id, focus: true } });
                            }
                          },
                          {
                            label: (
                              <span className="flex w-full min-w-0 flex-row items-center justify-start gap-2 text-start">
                                <i className="material-icons">delete</i>
                                {this.props.intl.formatMessage({ id: 'dashboardSharing.dropdown.delete', defaultMessage: 'Delete' })}
                              </span>
                            ),
                            onClick: (e) => {
                              this.props.closeTabDialog();
                              this.props.showDeleteDialog(tab.id);
                            }
                          }
                        ]}
                      />
                    </div>
                  }
                </div>
              )
            })}
          </div>
          {this.props.sharedTabs?.length > 0 && <>
            <nav className="mdl-navigation">
              <span
                className="mdl-navigation__link flex flex-row items-center gap-2 w-full truncate"
              >
                <i className="material-icons flex-shrink-0">hub</i>
                <span className="nav-link-text">
                  <FormattedMessage id='app.dash.nav.sharedDashboards' defaultMessage='SHARED WITH ME' />
                </span>
              </span>
            </nav>
            <div className="ag-dashboards-ctn">
              {this.props.sharedTabs?.sort((a, b) => {
                const numA = parseFloat(a?.name);
                const numB = parseFloat(b?.name);
                const hasNumA = !isNaN(numA);
                const hasNumB = !isNaN(numB);

                if (hasNumA && hasNumB) {
                  if (numA !== numB) return numA - numB;
                  return a.name?.localeCompare(b?.name);
                }
                if (hasNumA) return -1;
                if (hasNumB) return 1;
                return a.name?.localeCompare(b?.name);
              }).map(sharedTab => {
                return (
                  <div key={sharedTab.id} className={"dashboard-item"}>
                    <NavLink
                      to={{ pathname: `dashboard/${sharedTab.id}` }}
                      state={{ isSharedDash: true, tabName: sharedTab.name }}
                    >
                      <span key={sharedTab.id}>
                        {sharedTab.name}
                      </span>
                    </NavLink>
                    <div className="inline-block ml-2 rtl:ml-0 rtl:mr-2 align-middle">
                      <SharedTabDropdown
                        sharedTab={sharedTab}
                        userId={this.props.userId}
                        reloadSharedTabs={this.props.loadSharedTabs}
                        showToast={this.showToast}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </>}
          <nav className="mdl-navigation">
            {
              !this.props.isLockedUser &&
              <>
                <NavLink
                  className="mdl-navigation__link"
                  to='stations'
                  title={this.props.intl.formatMessage({ id: 'app.layout.station', defaultMessage: 'Stations' })}
                >
                  <i className="material-icons">router</i>
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.station' defaultMessage='Stations' />
                  </span>
                </NavLink>

                <NavLink
                  className="mdl-navigation__link"
                  to='dashboard-sharing'
                  title={this.props.intl.formatMessage({ id: "app.layout.shareDashboard", defaultMessage: 'SHARE DASHBOARDS' })}
                >
                  <i className="material-icons">share</i>
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.shareDashboard' defaultMessage='SHARE DASHBOARDS' />
                  </span>
                </NavLink>
                <NavLink
                  className="mdl-navigation__link"
                  to='alerts'
                  title={this.props.intl.formatMessage({ id: 'app.layout.alert', defaultMessage: 'Alerts' })}
                >
                  <i className="material-icons">warning</i>
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.alert' defaultMessage='Alerts' />
                  </span>
                </NavLink>
                <NavLink
                  className="mdl-navigation__link"
                  to='/'
                  title={this.props.intl.formatMessage({ id: 'app.layout.report', defaultMessage: 'Reports' })}
                >
                  <i className="material-icons">pie_chart</i>
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.report' defaultMessage='Reports' />
                  </span>
                </NavLink>
                <NavLink
                  className="mdl-navigation__link"
                  to='schedule'
                  title={this.props.intl.formatMessage({ id: 'app.layout.schedule', defaultMessage: 'Irrigation' })}
                >
                  <i className="material-icons">water_drop</i>
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.schedule' defaultMessage='Irrigation' />
                  </span>
                </NavLink>
                <NavLink
                  className="mdl-navigation__link"
                  to='agscout'
                  title={this.props.intl.formatMessage({ id: 'app.layout.AgScout', defaultMessage: 'AgScout' })}
                >
                  <img
                    src={agscoutLogo}
                    alt='AgScout Logo'
                    width='50px'
                    style={{
                      display: 'inline-block',
                      marginLeft: isRTL ? '9px' : '-3px',
                      marginRight: isRTL ? '-3px' : '9px',
                      width: '31px',
                      flexShrink: 0
                    }}
                  />
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.AgScout' defaultMessage='AgScout' />
                  </span>
                </NavLink>
                <NavLink
                  className="mdl-navigation__link"
                  to='dga'
                  title={this.props.intl.formatMessage({ id: 'app.layout.DGATitle', defaultMessage: 'Directorate/Department of Water Affairs' })}
                >
                  <i className="material-icons">cloud_upload</i>
                  <span className="nav-link-text">
                    <FormattedMessage id='app.layout.DGA' defaultMessage='DGA ' />
                  </span>
                </NavLink>
              </>
            }
            {
              !this.props.isLockedUser &&
              <NavLink
                className="mdl-navigation__link"
                to='tools'
                title={this.props.intl.formatMessage({ id: 'app.layout.tools', defaultMessage: 'Tools' })}
              >
                <i className="material-icons">build_circle</i>
                <span className="nav-link-text">
                  <FormattedMessage id='app.layout.tools' defaultMessage='Tools ' />
                </span>
              </NavLink>
            }
            <NavLink
              className="mdl-navigation__link"
              to='billing'
              title={this.props.intl.formatMessage({ id: 'app.layout.billing', defaultMessage: 'Billing' })}
            >
              <i className="material-icons">credit_card</i>
              <span className="nav-link-text">
                <FormattedMessage id='app.layout.billing' defaultMessage='Billing ' />
              </span>
            </NavLink>
            <NavLink
              className="mdl-navigation__link"
              to='profile'
              title={this.props.intl.formatMessage({ id: 'app.profile', defaultMessage: 'Profile' })}
            >
              <i className="material-icons">person</i>
              <span className="nav-link-text">
                <FormattedMessage id='app.profile' defaultMessage='Profile ' />
              </span>
            </NavLink>
          </nav>
          <div className="ag-logout-ctn">
            <a
              className="mdl-navigation__link ag-logout"
              onClick={(e) => { this.props.logout(e) }}
              href="/login"
            >
              <i className="material-icons">exit_to_app</i>
              <span className="nav-link-text">
                <FormattedMessage id='app.layout.logout' defaultMessage='Logout ' />
              </span>
            </a>
          </div>
        </div>

        
        <main className="mdl-layout__content">
          <Outlet />
        </main>
        <div style={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minWidth: '300px',
          maxWidth: '400px',
          alignItems: 'flex-end',
          pointerEvents: 'none'
        }}>
          {this.state.toasts.map(toast => (
            <div style={{ pointerEvents: 'auto', width: '100%' }} key={toast.id}>
              <Notification
                type={{ style: toast.type, icon: true }}
                closable={true}
                onClose={() => this.setState(prev => ({
                  toasts: prev.toasts.filter(t => t.id !== toast.id)
                }))}
                style={{
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <span style={{
                  color: '#fff',
                  fontWeight: 500
                }}>
                  {toast.message}
                </span>
              </Notification>
            </div>
          ))}
        </div>
      </div >
    );
  }
}

export default Layout;